'use strict';

define('vb/action/builtin/fireNotificationEventAction',[
  'vb/action/eventAction',
  'vb/action/action',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/constants',
], (EventAction, Action, Log, Utils, Constants) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  const logger = Log.getLogger('/vb/private/stateManagement/fireNotificationEventAction');

  const Modes = {
    PERSIST: 'persist',
    TRANSIENT: 'transient',
  };

  /**
   * Represents an action that fires a notification event.
   *
   * @extends EventAction
   * @category Action
   */
  class FireNotificationEventAction extends EventAction {
    /**
     * Fires a notification event based on the provided parameters.
     *
     * @param {Object} parameters - The parameters used to construct the event payload.
     * @param {string} [parameters.summary] - The event title or summary.
     * @param {string} [parameters.message] - The event text.
     * @param {'persist'|'transient'} [parameters.displayMode='persist'] - The display mode of the event.
     * @param {'error'|'warning'|'confirmation'|'info'} [parameters.type='error'] - The type of the event.
     * @param {string} [parameters.key] - An optional unique identifier for the event. If not provided, a random
     * 9-character alphanumeric ID will be generated.
     * @param {string} [parameters.target] - The target of the event.
     *
     * @returns {Outcome} An object containing the outcome of the event firing process.
     * @throws {Error} If the target is invalid or the event container is not available.
     */
    perform(parameters) {
      const payload = FireNotificationEventAction.createPayload(parameters);

      logger.info('FireNotificationEventAction', payload.type, payload.displayMode);

      if (parameters && !EventAction.isValidTarget(parameters.target)) {
        return EventAction.createFailureOutcome(`invalid "target" for event action: ${parameters.target}`);
      }

      if (this.eventContainer) {
        // ignore returned Promise; we don't wait for event processing
        const promiseOrNull = EventAction.fireEvent(this.eventContainer,
          Constants.NOTIFICATION_EVENT, payload, parameters);
        return promiseOrNull ? EventAction.createSuccessOutcome()
          : EventAction.createFailureOutcome(`Unable to find target: ${parameters.target}`);
      }

      return EventAction.createFailureOutcome(`unable to fire ${Constants.NOTIFICATION_EVENT} event, no page context`);
    }

    /**
     * Constructs the payload for a notification event with default values applied when necessary.
     *
     * @param {Object} options - Options for constructing the payload.
     * @param {string} [options.summary] - The event title or summary.
     * @param {string} [options.message] - The event text.
     * @param {'persist'|'transient'} [options.displayMode='persist'] - The display mode of the event.
     * @param {'error'|'warning'|'confirmation'|'info'} [options.type='error'] - The type of the event.
     * @param {string} [options.key] - An optional unique identifier for the event. If not provided, a random nine-
     * character alphanumeric ID will be generated.
     *
     * @returns {Object} The constructed payload object with properties:
     *   - `summary`: The event title or summary.
     *   - `message`: The event text.
     *   - `displayMode`: The display mode of the event.
     *   - `type`: The type of the event.
     *   - `key`: A unique identifier for the event.
     */
    static createPayload(options) {
      return {
        summary: options.summary || '',
        message: options.message || '',
        displayMode: options.displayMode || Modes.PERSIST,
        type: options.type || Constants.MessageType.ERROR,
        key: options.key || Utils.generateUniqueId(),
      };
    }
  }

  return FireNotificationEventAction;
});

